import { z } from 'zod';

// =====================
// IO BLOCKS
// =====================

const ConstantBlockConfigSchema = z.object({
  config_value: z.unknown(),
});

export type ConstantBlockConfig = z.infer<typeof ConstantBlockConfigSchema>;

const InputBlockConfigSchema = z.object({
  json_schema: z.record(z.unknown()),
});

export type InputBlockConfig = z.infer<typeof InputBlockConfigSchema>;

const OutputBlockConfigSchema = z.object({
  name: z.string().default('output'),
  json_schema: z.record(z.unknown()).optional(),
});

export type OutputBlockConfig = z.infer<typeof OutputBlockConfigSchema>;

const DatasetBlockConfigSchema = z.object({
  dataset_id: z.string(),
});

export type DatasetBlockConfig = z.infer<typeof DatasetBlockConfigSchema>;

const FileBlockConfigSchema = z.object({
  file_id: z.string().optional(),
});

export type FileBlockConfig = z.infer<typeof FileBlockConfigSchema>;

const ArtifactBlockConfigSchema = z.object({
  json_schema: z.record(z.unknown()).optional(),
  name: z.string().default('artifact'),
  description: z.string().optional(),
  include_metadata: z.boolean().default(true),
});

export type ArtifactBlockConfig = z.infer<typeof ArtifactBlockConfigSchema>;

// =====================
// FLOW BLOCKS
// =====================

const IdentityBlockConfigSchema = z.object({
  // No configuration needed
});

export type IdentityBlockConfig = z.infer<typeof IdentityBlockConfigSchema>;

const ZipBlockConfigSchema = z.object({
  combine_strategy: z
    .enum(['merge_objects', 'create_array'])
    .default('merge_objects'),
});

export type ZipBlockConfig = z.infer<typeof ZipBlockConfigSchema>;

const MapBlockConfigSchema = z.object({
  target_block_type: z.string(),
  target_config: z.record(z.unknown()).default({}),
  max_concurrency: z.number().default(5),
});

export type MapBlockConfig = z.infer<typeof MapBlockConfigSchema>;

const JoinOperationSchema = z.object({
  dataset: z.string(),
  left_key: z.string(),
  right_key: z.string(),
  join_type: z.enum(['inner', 'left', 'right', 'full']).default('inner'),
});

const ConflictResolutionSchema = z.object({
  strategy: z.enum(['prefix', 'suffix', 'explicit']).default('prefix'),
  left_modifier: z.string().default(''),
  right_modifier: z.string().default(''),
});

const OutputColumnSchema = z.object({
  name: z.string(),
  source: z.string(),
  type: z.enum(['string', 'integer', 'number', 'boolean', 'null', 'object']),
  nullable: z.boolean().default(false),
});

const JoinBlockConfigSchema = z.object({
  base_dataset: z.string(),
  joins: z.array(JoinOperationSchema),
  output_columns: z.array(OutputColumnSchema),
  dataset_names: z.record(z.string()).optional(),
  conflict_resolution: ConflictResolutionSchema.optional(),
});

export type JoinBlockConfig = z.infer<typeof JoinBlockConfigSchema>;

// =====================
// AI BLOCKS
// =====================

const LLMBlockConfigSchema = z.object({
  provider: z.enum([
    'openai',
    'anthropic',
    'cohere',
    'groq',
    'cerebras',
    'google',
    'vertexai',
  ]),
  model: z.enum(['small', 'medium', 'large']).default('small'),
  model_override: z.string().optional(),
  temperature: z.number().min(0).max(2).default(0.2),
  max_tokens: z.number().positive().max(100000).default(8192),
  output_schema: z.record(z.unknown()).optional(),
});

export type LLMBlockConfig = z.infer<typeof LLMBlockConfigSchema>;

const EmbeddingsBlockConfigSchema = z.object({
  model: z.string().default('sentence-transformers/static-retrieval-mrl-en-v1'),
  batch_size: z.number().min(1).max(1000).default(100),
  normalize: z.boolean().default(true),
});

export type EmbeddingsBlockConfig = z.infer<typeof EmbeddingsBlockConfigSchema>;

const SemanticFilterBlockConfigSchema = z.object({
  embedding_model: z
    .string()
    .default('sentence-transformers/static-retrieval-mrl-en-v1'),
  filter_strategy: z
    .literal('cosine_similarity_top_k')
    .default('cosine_similarity_top_k'),
  top_k: z.number().min(1).max(1000).default(10),
  similarity_threshold: z.number().min(0).max(1).default(0),
  batch_size: z.number().min(1).max(1000).default(100),
});

export type SemanticFilterBlockConfig = z.infer<
  typeof SemanticFilterBlockConfigSchema
>;

// =====================
// MOLECULE BLOCKS
// =====================

const ClauseExtractionAgentBlockConfigSchema = z.object({
  instructions: z.string(),
});

export type ClauseExtractionAgentBlockConfig = z.infer<
  typeof ClauseExtractionAgentBlockConfigSchema
>;

const RiskTierDefinitionsSchema = z.object({
  catastrophic_description: z.string().optional(),
  extreme_description: z.string().optional(),
  high_description: z.string().optional(),
  medium_description: z.string().optional(),
  low_description: z.string().optional(),
});

const LegalVarianceDeterminationBlockConfigSchema = z.object({
  risk_tier_definitions: RiskTierDefinitionsSchema.optional(),
});

export type RiskTierDefinitions = z.infer<typeof RiskTierDefinitionsSchema>;

export type LegalVarianceDeterminationBlockConfig = z.infer<
  typeof LegalVarianceDeterminationBlockConfigSchema
>;

// =====================
// TRANSFORM BLOCKS
// =====================

const StringTemplateBlockConfigSchema = z.object({
  template: z.string(),
  strip_whitespace: z.boolean().default(true),
});

export type StringTemplateBlockConfig = z.infer<
  typeof StringTemplateBlockConfigSchema
>;

const TextSplitterBlockConfigSchema = z.object({
  chunk_size: z.number().positive().max(10000).default(1000),
  chunk_overlap: z.number().min(0).default(200),
  separators: z.array(z.string()).optional(),
});

export type TextSplitterBlockConfig = z.infer<
  typeof TextSplitterBlockConfigSchema
>;

const TransformBlockConfigSchema = z.object({
  operations: z.array(z.record(z.unknown())),
});

export type TransformBlockConfig = z.infer<typeof TransformBlockConfigSchema>;

const PDFReaderBlockConfigSchema = z.object({
  // No configuration needed
});

export type PDFReaderBlockConfig = z.infer<typeof PDFReaderBlockConfigSchema>;

const DocxReaderBlockConfigSchema = z.object({
  // No configuration needed
});

export type DocxReaderBlockConfig = z.infer<typeof DocxReaderBlockConfigSchema>;

const JsonTemplateBlockConfigSchema = z.object({
  template: z.union([z.record(z.unknown()), z.array(z.unknown())]),
});

export type JsonTemplateBlockConfig = z.infer<
  typeof JsonTemplateBlockConfigSchema
>;

// Base block fields that all blocks share
const BaseBlockSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
});

// Discriminated union for all block types
export const PipelineBlockSchema = z.discriminatedUnion('type', [
  // IO Blocks
  BaseBlockSchema.extend({
    type: z.literal('constant'),
    config: ConstantBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('input'),
    config: InputBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('output'),
    config: OutputBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('dataset'),
    config: DatasetBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('file'),
    config: FileBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('artifact'),
    config: ArtifactBlockConfigSchema,
  }),

  // Flow Blocks
  BaseBlockSchema.extend({
    type: z.literal('identity'),
    config: IdentityBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('zip'),
    config: ZipBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('map'),
    config: MapBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('join'),
    config: JoinBlockConfigSchema,
  }),

  // AI Blocks
  BaseBlockSchema.extend({
    type: z.literal('llm'),
    config: LLMBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('embeddings'),
    config: EmbeddingsBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('semantic_filter'),
    config: SemanticFilterBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('clause_extraction_agent'),
    config: ClauseExtractionAgentBlockConfigSchema,
  }),
  // Legacy alias for clause_extraction_agent
  BaseBlockSchema.extend({
    type: z.literal('clause_extraction'),
    config: ClauseExtractionAgentBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('legal_variance_determination'),
    config: LegalVarianceDeterminationBlockConfigSchema,
  }),

  // Transform Blocks
  BaseBlockSchema.extend({
    type: z.literal('string_template'),
    config: StringTemplateBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('text_splitter'),
    config: TextSplitterBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('transform'),
    config: TransformBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('pdf_reader'),
    config: PDFReaderBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('docx_reader'),
    config: DocxReaderBlockConfigSchema,
  }),
  BaseBlockSchema.extend({
    type: z.literal('json_template'),
    config: JsonTemplateBlockConfigSchema,
  }),
]);

export type PipelineBlock = z.infer<typeof PipelineBlockSchema>;

// Connection schema based on actual YAML structure
export const PipelineConnectionSchema = z.object({
  source_port: z.string(),
  target_port: z.string(),
  source_block_id: z.string(),
  target_block_id: z.string(),
});

export type PipelineConnection = z.infer<typeof PipelineConnectionSchema>;

// Metadata schema
export const PipelineMetadataSchema = z
  .object({
    name: z.string().optional(),
    version: z.string().optional(),
    description: z.string().optional(),
  })
  .passthrough();

export type PipelineMetadata = z.infer<typeof PipelineMetadataSchema>;

// Pipeline configuration schema based on actual YAML structure
export const PipelineConfigurationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    version: z.string().default('1.0.0'),
    blocks: z.record(PipelineBlockSchema),
    connections: z.array(PipelineConnectionSchema).default([]),
    metadata: PipelineMetadataSchema.optional(),
  })
  .passthrough(); // Allow additional properties for future extensibility

// Type for pipeline configuration
export type PipelineConfiguration = z.infer<typeof PipelineConfigurationSchema>;

// Re-export validation types that are still used
export {
  type PipelineValidationResult,
  type PipelineValidationError,
  type PipelineValidationWarning,
  PipelineValidationResultSchema,
  PipelineValidationErrorSchema,
  PipelineValidationWarningSchema,
} from './schemas/pipeline-json-schema';
